import * as React from 'react';
import { SearchStringContainer } from '../SearchStringContainer';
import { DesktopFiltersContainer } from '../DesktopFiltersContainer';

import styles from './SearchStringBlockContainer.css';
import { Button } from '@cian/ui-kit';
import { IconActionMap16 } from '@cian/ui-kit-design-tokens/icons';
import { useFiltersUpdatedEvent } from '../../hooks/filters';
import { EPageKind } from '../../types/page';
import { trackSearchButtonClick, trackSearchOnMapButtonClick } from '../../components/FiltersSearchButtons/tracking';
import { Experiment } from '@cian/experiments/ab';
import { AUD_DESKTOP_BX206_SEARCH_STRING_DESKTOP } from '../../constants/experiments';
import { useApplicationContext } from '../../utils/applicationContext';
import { ESearchType } from '../../types/searchString';
import { mergeStyles } from '@cian/utils';

const FILTERS_MODAL_OPEN_EVENT = 'filter:click';
const SEARCH_UPDATED_EVENT = 'search:updated';

type TSearchUpdatedEventDetail = {
  url: string;
  type: ESearchType;
};

export const SearchStringBlockContainer: React.FC = () => {
  const { microfrontendsAPI } = useApplicationContext();
  const { searchMeta } = useFiltersUpdatedEvent();

  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsLoading(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  React.useEffect(() => {
    const onFiltersModalOpen = () => {
      if (microfrontendsAPI) {
        microfrontendsAPI.getDesktopFiltersMicrofrontendAPI().openAllFiltersModal();
      }
    };

    addEventListener(FILTERS_MODAL_OPEN_EVENT, onFiltersModalOpen);

    return () => {
      removeEventListener(FILTERS_MODAL_OPEN_EVENT, onFiltersModalOpen);
    };
  }, [microfrontendsAPI]);

  React.useEffect(() => {
    const onSearchUpdated = ({ detail: { type, url } }: CustomEvent<TSearchUpdatedEventDetail>) => {
      setIsLoading(true);
      if (type === ESearchType.List) {
        trackSearchButtonClick(EPageKind.Default, url);
      } else {
        trackSearchOnMapButtonClick(EPageKind.Default, url);
      }

      window.location.href = url;
    };

    addEventListener(SEARCH_UPDATED_EVENT, onSearchUpdated);

    return () => {
      removeEventListener(SEARCH_UPDATED_EVENT, onSearchUpdated);
    };
  }, []);

  const onSearchListClick = () => {
    if (microfrontendsAPI) {
      microfrontendsAPI.getSearchStringMicrofrontendAPI().clickSearchSuggestion(ESearchType.List);
    }

    if (searchMeta?.fullUrl) {
      setIsLoading(true);
      trackSearchButtonClick(EPageKind.Default, searchMeta.fullUrl);
      window.location.href = searchMeta.fullUrl;
    }
  };

  const onSearchMapClick = () => {
    if (microfrontendsAPI) {
      microfrontendsAPI.getSearchStringMicrofrontendAPI().clickSearchSuggestion(ESearchType.Map);
    }

    if (searchMeta?.mapUrl) {
      setIsLoading(true);
      trackSearchOnMapButtonClick(EPageKind.Default, searchMeta.mapUrl);
      window.location.href = searchMeta.mapUrl;
    }
  };

  return (
    <>
      <Experiment name={AUD_DESKTOP_BX206_SEARCH_STRING_DESKTOP} group="B">
        <div className={mergeStyles(styles['block-container'], styles['block-container-group-b']).className}>
          <SearchStringContainer />
        </div>
      </Experiment>

      <Experiment name={AUD_DESKTOP_BX206_SEARCH_STRING_DESKTOP} group="C">
        <SearchStringContainer />
        <div style={{ display: 'none' }}>
          <DesktopFiltersContainer />
        </div>
      </Experiment>

      <Experiment name={AUD_DESKTOP_BX206_SEARCH_STRING_DESKTOP} group="D">
        <div className={styles['block-container']}>
          <div>
            <SearchStringContainer />
            <DesktopFiltersContainer />
          </div>
          <div className={styles['button-container']}>
            <Button onClick={onSearchListClick} loading={isLoading}>
              Найти
            </Button>
            <Button
              theme="fill_secondary"
              afterIcon={<IconActionMap16 color="icon-main-default" />}
              onClick={onSearchMapClick}
            >
              На карте
            </Button>
          </div>
        </div>
      </Experiment>
    </>
  );
};
